'use client';

import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { getAllowanceKey } from '@revoke.cash/core/allowances';
import { getEventKey } from '@revoke.cash/core/events';
import { type Exploit, getExploitStatus } from '@revoke.cash/core/exploits';
import { isNullish } from '@revoke.cash/core/utils';
import { useQuery } from '@tanstack/react-query';
import EmptyState from 'components/common/EmptyState';
import ErrorDisplay from 'components/common/ErrorDisplay';
import Loader from 'components/common/Loader';
import { useAddress } from 'lib/hooks/page-context/AddressIdentityContext';
import {
  useAddressAllowances,
  useAddressEvents,
  useAddressPageContext,
} from 'lib/hooks/page-context/AddressPageContext';
import analytics from 'lib/utils/analytics';
import ExploitStatus from './ExploitStatus';

interface Props {
  exploit: Exploit;
}

const ExploitChecker = ({ exploit }: Props) => {
  const { address } = useAddress();
  const { selectedChainId } = useAddressPageContext();
  const { allowances, error, isLoading } = useAddressAllowances();
  const { events } = useAddressEvents();

  // We wrap the status getting in a useQuery mainly so that we can make sure the analytics tracking is handled correctly
  const { data: exploitResult } = useQuery({
    queryKey: ['exploit-status', exploit.slug, allowances?.map(getAllowanceKey), events?.map(getEventKey)],
    queryFn: () => {
      const result = getExploitStatus(events!, allowances!, exploit);
      analytics.track('Exploit Checked', {
        exploit: exploit.slug,
        account: address,
        chainId: selectedChainId,
        status: result.status,
      });
      return result;
    },
    enabled: !isNullish(address) && !isNullish(events) && !isNullish(allowances) && !isNullish(selectedChainId),
  });

  if (!address || !selectedChainId) return null;

  return (
    <Loader isLoading={isLoading} className="exploit-checker-loader">
      {/* We use "safe" status as default for formatting the loader */}
      {error ? (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800">
          <EmptyState icon={ExclamationTriangleIcon} iconClassName="text-red-500 dark:text-red-400">
            <ErrorDisplay error={error} withIcon={false} className="whitespace-normal" />
          </EmptyState>
        </div>
      ) : (
        <ExploitStatus
          status={exploitResult?.status ?? 'safe'}
          matchedSpenders={exploitResult?.matchedSpenders ?? []}
          address={address}
          chainId={selectedChainId}
        />
      )}
    </Loader>
  );
};

export default ExploitChecker;
